import React from 'react';
import { Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface CrmSuggestion {
    id: string; type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string; last_name?: string; company_name?: string;
    phone?: string; email?: string;
}

/**
 * Ouverture d'un dossier de Suivi de prestation depuis le coup de téléphone — le strict minimum, sur le
 * même principe que NouveauDossierModal.tsx côté Suivi privatisation : nom, email, téléphone, particulier
 * ou professionnel, et une note libre pour ce qui a été dit au téléphone. Le devis se compose après, dans
 * le pipeline — pas ici.
 *
 * Recherche CRM en tapant le nom/prénom (particulier) ou la société (pro) — demande d'Emeni (2026-09-05) :
 * retrouver un client déjà connu au lieu de ressaisir ses coordonnées à chaque appel. Même pattern que
 * RdvProModule.tsx (moduleApi.searchCRMContacts, debounce 250ms) ; le dossier reste rattaché au contact
 * CRM choisi (crm_contact_id) plutôt que d'en recréer un doublon à la création.
 */
export function NouveauDossierPrestationModal({ onClose, onCree }: { onClose: () => void; onCree: (id: string) => void }) {
    const [type, setType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [form, setForm] = React.useState({ first_name: '', last_name: '', company_name: '', email: '', phone: '', notes_appel: '' });
    const [crmContactId, setCrmContactId] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    // Champs obligatoires réglés dans « Façon de travailler », séparément pour particuliers et professionnels.
    // Tant que la configuration n'est pas lue (ou en cas d'échec), l'e-mail reste obligatoire comme avant ; le serveur
    // refait de toute façon le contrôle.
    const [obligatoires, setObligatoires] = React.useState<{ part: string[]; pro: string[] }>({ part: ['email'], pro: ['email'] });
    React.useEffect(() => {
        moduleApi.getFaconDeTravailler().then((r: any) => { if (r?.obligatoires) setObligatoires(r.obligatoires); }).catch(() => { /* garde le repli */ });
    }, []);
    const requis = (champ: string) => (type === 'PROFESSIONNEL' ? obligatoires.pro : obligatoires.part).includes(champ);

    const [lookupField, setLookupField] = React.useState<'first_name' | 'last_name' | 'company_name' | null>(null);
    const [lookupTerm, setLookupTerm] = React.useState('');
    const [suggestions, setSuggestions] = React.useState<CrmSuggestion[]>([]);
    const [searching, setSearching] = React.useState(false);

    React.useEffect(() => {
        const q = lookupTerm.trim();
        if (q.length < 2) { setSuggestions([]); setSearching(false); return; }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const items = await moduleApi.searchCRMContacts(q);
                if (!cancelled) setSuggestions(Array.isArray(items) ? items : []);
            } catch { if (!cancelled) setSuggestions([]); } finally { if (!cancelled) setSearching(false); }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [lookupTerm]);

    const triggerLookup = (field: 'first_name' | 'last_name' | 'company_name', value: string) => {
        setCrmContactId('');
        setLookupField(field);
        setLookupTerm(value);
    };

    const applyCRMContact = (s: CrmSuggestion) => {
        setType(s.type === 'PROFESSIONNEL' ? 'PROFESSIONNEL' : 'PRIVÉ');
        setForm({
            first_name: s.first_name || '', last_name: s.last_name || '', company_name: s.company_name || '',
            email: s.email || '', phone: s.phone || '', notes_appel: form.notes_appel,
        });
        setCrmContactId(s.id);
        setSuggestions([]);
        setLookupTerm('');
        setLookupField(null);
    };

    const renderSuggestions = (field: 'first_name' | 'last_name' | 'company_name') => {
        if (lookupField !== field) return null;
        if (!searching && suggestions.length === 0) return null;
        return (
            <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-48 overflow-y-auto">
                {searching && <div className="px-3 py-2 text-[11px] text-content-muted">Recherche CRM...</div>}
                {!searching && suggestions.map((s) => (
                    <button key={s.id} type="button" onMouseDown={() => applyCRMContact(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                        <div className="font-semibold truncate">{s.type === 'PROFESSIONNEL' ? (s.company_name || 'Entreprise') : [s.first_name, s.last_name].filter(Boolean).join(' ')}</div>
                        {s.phone && <div className="text-[11px] text-content-muted">{s.phone}</div>}
                    </button>
                ))}
            </div>
        );
    };

    const submit = async () => {
        setErreur('');
        if (type === 'PROFESSIONNEL' && !form.company_name.trim()) { setErreur('Nom de la société requis.'); return; }
        if (type === 'PRIVÉ' && !form.last_name.trim()) { setErreur('Nom du client requis.'); return; }
        if (requis('email') && !form.email.trim()) { setErreur("Email requis — c'est par lui que partira le devis."); return; }
        if (requis('telephone') && !form.phone.trim()) { setErreur('Téléphone requis.'); return; }
        setSaving(true);
        try {
            const res: any = await moduleApi.createPrestationDossier({ type, ...form, crm_contact_id: crmContactId || undefined });
            if (res?.error) { setErreur(res.error); return; }
            onCree(res.id);
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la création.');
        } finally {
            setSaving(false);
        }
    };

    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    return (
        <Modal open onClose={onClose} title="Nouveau dossier" maxWidth="max-w-lg">
            <div className="p-5 space-y-4">
                <div className="flex gap-2">
                    {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                        <button key={t} type="button" onClick={() => setType(t)}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${type === t ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted hover:text-content'}`}>
                            {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                        </button>
                    ))}
                </div>

                {type === 'PROFESSIONNEL' ? (
                    <div className="relative">
                        <label className="text-xs font-bold text-content-muted">Société</label>
                        <input className={champ} value={form.company_name}
                            onChange={(e) => { setForm({ ...form, company_name: e.target.value }); triggerLookup('company_name', e.target.value); }}
                            onFocus={() => setLookupField('company_name')} />
                        {renderSuggestions('company_name')}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="relative">
                            <label className="text-xs font-bold text-content-muted">Prénom</label>
                            <input className={champ} value={form.first_name}
                                onChange={(e) => { setForm({ ...form, first_name: e.target.value }); triggerLookup('first_name', e.target.value); }}
                                onFocus={() => setLookupField('first_name')} />
                            {renderSuggestions('first_name')}
                        </div>
                        <div className="relative">
                            <label className="text-xs font-bold text-content-muted">Nom</label>
                            <input className={champ} value={form.last_name}
                                onChange={(e) => { setForm({ ...form, last_name: e.target.value }); triggerLookup('last_name', e.target.value); }}
                                onFocus={() => setLookupField('last_name')} />
                            {renderSuggestions('last_name')}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-bold text-content-muted">Email{requis('email') ? ' *' : ''}</label>
                        <input type="email" className={champ} value={form.email} aria-required={requis('email')} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-content-muted">Téléphone{requis('telephone') ? ' *' : ''}</label>
                        <input className={champ} value={form.phone} aria-required={requis('telephone')} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold text-content-muted">Ce qui a été dit au téléphone</label>
                    <textarea rows={3} className={`${champ} resize-none`} value={form.notes_appel} onChange={(e) => setForm({ ...form, notes_appel: e.target.value })} placeholder="Besoin décrit par le client..." />
                </div>

                {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
                <Button onClick={submit} disabled={saving}>
                    {saving ? <Loader2 size={15} className="animate-spin" /> : null} Créer le dossier
                </Button>
            </div>
        </Modal>
    );
}
